import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { COUNTRIES, findCountry } from "../data/countries";

const countryName = (c, lang) => {
  if (!c || lang === "sq") return c?.name;
  try {
    return new Intl.DisplayNames([lang], { type: "region" }).of(c.code.toUpperCase()) || c.name;
  } catch (_) {
    return c.name;
  }
};

/**
 * Phone input with country flag + dial-code prefix. `value` is the full
 * E.164-style string (e.g. "+355 694 087 888"); `country` / `onCountryChange`
 * track the ISO2 country code so the parent can persist it.
 */
export const PhoneInput = ({
  value = "",
  country = "al",
  onChange,
  onCountryChange,
  placeholder = "69 XXX XXXX",
  required = false,
  "data-testid": dataTestId = "phone-input",
}) => {
  const { tx, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapRef = useRef(null);
  const current = findCountry(country);
  // Extract just the local part (after the dial code) for the visible input.
  const localPart = value.startsWith(current.dial)
    ? value.slice(current.dial.length).trim()
    : value.replace(/^\+\d+\s*/, "").trim();

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const emit = (newDial, newLocal) => {
    const local = newLocal.replace(/[^\d\s-]/g, "");
    onChange && onChange(local ? `${newDial} ${local}` : newDial);
  };

  const filtered = filter
    ? COUNTRIES.filter((c) => countryName(c, lang).toLowerCase().includes(filter.toLowerCase()) || c.dial.includes(filter))
    : COUNTRIES;

  return (
    <div ref={wrapRef} className="relative" data-testid={dataTestId}>
      <div className="flex border border-slate-200 rounded-lg bg-slate-50 focus-within:ring-2 focus-within:ring-[#3CD4E1]/30 focus-within:border-[#3CD4E1] transition-all">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 px-2.5 py-2.5 border-r border-slate-200 hover:bg-slate-100 rounded-l-lg text-sm"
          data-testid={`${dataTestId}-flag`}
          aria-label={tx("Zgjidh vendin")}
        >
          <span className={`fi fi-${current.code}`} style={{ width: 20, height: 15, borderRadius: 2 }}></span>
          <span className="text-slate-400 text-xs">▾</span>
        </button>
        <span className="flex items-center pl-2.5 text-sm text-slate-500 select-none">{current.dial}</span>
        <input
          type="tel"
          value={localPart}
          onChange={(e) => emit(current.dial, e.target.value)}
          placeholder={placeholder}
          required={required}
          className="flex-1 bg-transparent px-2 py-2.5 text-sm outline-none"
          data-testid={`${dataTestId}-number`}
        />
      </div>

      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 w-72 max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="sticky top-0 bg-white border-b border-slate-100 p-2">
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={tx("Kërko...")}
              className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#3CD4E1]"
            />
          </div>
          {filtered.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => {
                onCountryChange && onCountryChange(c.code);
                emit(c.dial, localPart);
                setOpen(false);
                setFilter("");
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left ${c.code === current.code ? "bg-[#3CD4E1]/10" : ""}`}
            >
              <span className={`fi fi-${c.code}`} style={{ width: 20, height: 15, borderRadius: 2 }}></span>
              <span className="flex-1 text-[#003F72]">{countryName(c, lang)}</span>
              <span className="text-slate-400 text-xs">{c.dial}</span>
            </button>
          ))}
          {filtered.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">{tx("Nuk u gjet")}</div>}
        </div>
      )}
    </div>
  );
};

/**
 * Standalone country selector for the "Shteti" field (no dial code).
 */
export const CountrySelect = ({ value = "al", onChange, "data-testid": dataTestId = "country-select" }) => {
  const { tx, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapRef = useRef(null);
  const current = findCountry(value);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = filter
    ? COUNTRIES.filter((c) => countryName(c, lang).toLowerCase().includes(filter.toLowerCase()))
    : COUNTRIES;

  return (
    <div ref={wrapRef} className="relative" data-testid={dataTestId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 border border-slate-200 rounded-lg bg-slate-50 px-3 py-2.5 text-[11px] text-left hover:bg-slate-100"
      >
        <span className={`fi fi-${current.code}`} style={{ width: 20, height: 15, borderRadius: 2 }}></span>
        <span className="flex-1 text-[#0A0D10]">{countryName(current, lang)}</span>
        <span className="text-slate-400 text-xs">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="sticky top-0 bg-white border-b border-slate-100 p-2">
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={tx("Kërko...")}
              className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm outline-none focus:border-[#3CD4E1]"
            />
          </div>
          {filtered.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => { onChange && onChange(c.code); setOpen(false); setFilter(""); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left ${c.code === current.code ? "bg-[#3CD4E1]/10" : ""}`}
            >
              <span className={`fi fi-${c.code}`} style={{ width: 20, height: 15, borderRadius: 2 }}></span>
              <span className="text-[#003F72]">{countryName(c, lang)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
